import { useEffect, useMemo, useState, useCallback } from 'react'
import { Markdown } from '@neo-agents/ui'
import { toast } from 'sonner'
import { Badge } from '../../../../components/ui/badge'
import { Button } from '../../../../components/ui/button'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../../../components/ui/pagination'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog'
import { Alert, AlertDescription } from '../../../../components/ui/alert'
import { Skeleton } from '../../../../components/ui/skeleton'
import { TableLoadingState } from '../../../../components/ui/table-loading-state'
import { useAuth } from '../../../../contexts/AuthContext'
import { ADMIN_PAGE_CONTENT_CLASSNAME } from '../layoutStyles'
import {
  fetchFlaggedList,
  fetchFlaggedMessages,
  reviewFlaggedItem,
} from '../../../../api/backend'
import type { FlaggedItem, FlaggedMessage } from '../../../../api/backend'
import {
  FLAGGED_PAGE_SIZE,
  buildFlaggedPageNumbers,
  clampFlaggedPage,
  getFlaggedPageCount,
  getPaginatedFlaggedItems,
  shouldShowFlaggedPagination,
} from './pagination'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

function formatTime(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
}

function formatSeverityLabel(value?: string | null) {
  switch (value) {
    case 'ATTENTION':
      return 'Attention'
    case 'WARNING':
      return 'Warning'
    case 'RESTRICTED':
      return 'Restricted'
    default:
      return value ?? '—'
  }
}

function getMessageContent(msg: FlaggedMessage): string {
  if (msg.content) return msg.content
  const textPart = msg.parts?.find((p) => p.type !== 'IMAGE')
  return textPart?.content ?? ''
}

// ─── View Modal ──────────────────────────────────────────────────────────────

interface ViewModalProps {
  item: FlaggedItem
  onClose: () => void
  onReviewed: (id: string, action: 'dismiss' | 'unblock') => void
}

function ViewModal({ item, onClose, onReviewed }: ViewModalProps) {
  const { getAccessToken } = useAuth()
  const [messages, setMessages] = useState<FlaggedMessage[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isReviewing, setIsReviewing] = useState(false)

  useEffect(() => {
    const token = getAccessToken()
    if (!token) return
    setIsLoading(true)
    setError(null)
    fetchFlaggedMessages(token, item.chatId)
      .then(setMessages)
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load messages')
      })
      .finally(() => setIsLoading(false))
  }, [getAccessToken, item.chatId])

  const handleAction = async (action: 'dismiss' | 'unblock') => {
    const token = getAccessToken()
    if (!token) return
    setIsReviewing(true)
    try {
      await reviewFlaggedItem(token, item.id, { action, type: 'message' })
      onReviewed(item.id, action)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply review action')
    } finally {
      setIsReviewing(false)
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col gap-0 p-0 overflow-hidden" aria-describedby={undefined}>
        <DialogHeader className="px-6 pt-5 pb-4 border-b-2 border-nb-border shrink-0 pr-14">
          <DialogTitle>Flagged Conversation</DialogTitle>
          <p className="text-xs text-foreground/60 mt-0.5">{item.chatTitle}</p>
        </DialogHeader>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          {isLoading ? (
            <div className="flex flex-col gap-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : messages.length === 0 ? (
            <p className="text-center py-12 text-sm text-foreground/40">No messages found.</p>
          ) : (
            <div className="flex flex-col gap-3">
              {messages.map((msg) => {
                const isUser = msg.role.toUpperCase() === 'USER'
                const isFlagged = msg.moderationAction === 'FLAGGED'
                const ts = msg.createdAt ?? msg.timestamp
                return (
                  <div
                    key={msg.id}
                    className={`w-full rounded-base border-2 px-4 py-3 text-sm ${
                      isFlagged
                        ? 'border-destructive bg-destructive/5'
                        : 'border-nb-border bg-background'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className={`text-xs font-heading ${isUser ? 'text-main-foreground bg-main px-1.5 py-0.5 rounded-sm' : 'text-foreground/60'}`}>
                        {isUser ? 'Student' : 'AI'}
                      </span>
                      <div className="flex items-center gap-2">
                        {isFlagged && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-heading uppercase tracking-wider bg-destructive text-white">
                            Block
                          </span>
                        )}
                        {ts && (
                          <span className="text-[10px] text-foreground/40">{formatTime(ts)}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-sm">
                      <Markdown id={msg.id}>{getMessageContent(msg)}</Markdown>
                    </div>
                    {isFlagged && (msg.originalContent || msg.moderationCategory) && (
                      <div className="mt-3 pt-3 border-t border-destructive/20 space-y-2">
                        {msg.originalContent && (
                          <div>
                            <span className="text-[10px] font-heading uppercase tracking-wider text-destructive">Original LLM Response</span>
                            <div className="text-sm text-foreground/60 mt-0.5">
                              <Markdown id={`${msg.id}-original`}>{msg.originalContent}</Markdown>
                            </div>
                          </div>
                        )}
                        {msg.moderationCategory && (
                          <div>
                            <span className="text-[10px] font-heading uppercase tracking-wider text-destructive">Category</span>
                            <p className="text-sm text-foreground/60 mt-0.5">{msg.moderationCategory}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t-2 border-nb-border shrink-0">
          <div className="text-xs text-foreground/60">
            Status:{' '}
            {item.reviewed ? (
              <span className="text-green-700 dark:text-green-400 font-base">Reviewed</span>
            ) : (
              <span className="text-amber-600 dark:text-amber-400 font-base">Pending</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="neutral" size="sm" onClick={onClose}>
              Close
            </Button>
            {item.reviewStatus !== 'DISMISSED' && (
              <Button size="sm" onClick={() => handleAction('dismiss')} disabled={isReviewing}>
                {isReviewing ? 'Updating...' : 'Dismiss'}
              </Button>
            )}
            {item.severity === 'RESTRICTED' && (
              <Button variant="neutral" size="sm" onClick={() => handleAction('unblock')} disabled={isReviewing}>
                Unblock
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ─── Flagged Page ────────────────────────────────────────────────────────────

export default function FlaggedPage() {
  const { getAccessToken } = useAuth()
  const [items, setItems] = useState<FlaggedItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [viewingItem, setViewingItem] = useState<FlaggedItem | null>(null)
  const [severityFilter, setSeverityFilter] = useState<'ALL' | 'ATTENTION' | 'WARNING' | 'RESTRICTED'>('ALL')
  const [flaggedPage, setFlaggedPage] = useState(0)

  const load = useCallback(() => {
    const token = getAccessToken()
    if (!token) return
    setIsLoading(true)
    setError(null)
    fetchFlaggedList(token)
      .then(setItems)
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load flagged conversations')
      })
      .finally(() => setIsLoading(false))
  }, [getAccessToken])

  useEffect(() => { load() }, [load])

  const handleReviewed = (id: string, action: 'dismiss' | 'unblock') => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item
        return {
          ...item,
          reviewed: true,
          reviewStatus: action === 'dismiss' ? 'DISMISSED' : 'REVIEWED',
        }
      }),
    )
    setViewingItem(null)
  }

  const filteredItems = useMemo(
    () => items.filter((item) =>
      severityFilter === 'ALL' ? true : item.severity === severityFilter,
    ),
    [items, severityFilter],
  )
  const flaggedPageCount = getFlaggedPageCount(filteredItems.length)
  const safeFlaggedPage = clampFlaggedPage(flaggedPage, filteredItems.length)
  const flaggedPageNumbers = useMemo(
    () => buildFlaggedPageNumbers(flaggedPageCount, safeFlaggedPage),
    [flaggedPageCount, safeFlaggedPage],
  )
  const paginatedItems = useMemo(
    () => getPaginatedFlaggedItems(filteredItems, safeFlaggedPage),
    [filteredItems, safeFlaggedPage],
  )
  const flaggedPageStart = filteredItems.length === 0
    ? 0
    : safeFlaggedPage * FLAGGED_PAGE_SIZE + 1
  const flaggedPageEnd = Math.min(filteredItems.length, flaggedPageStart + FLAGGED_PAGE_SIZE - 1)

  useEffect(() => {
    if (flaggedPage !== safeFlaggedPage) {
      setFlaggedPage(safeFlaggedPage)
    }
  }, [flaggedPage, safeFlaggedPage])

  const filterCounts = {
    ALL: items.length,
    ATTENTION: items.filter((item) => item.severity === 'ATTENTION').length,
    WARNING: items.filter((item) => item.severity === 'WARNING').length,
    RESTRICTED: items.filter((item) => item.severity === 'RESTRICTED').length,
  }

  return (
    <div className={ADMIN_PAGE_CONTENT_CLASSNAME}>
      <div className="mb-6">
        <p className="text-sm text-foreground/60">
          {!isLoading && !error
            ? `${filteredItems.length} safety item${filteredItems.length !== 1 ? 's' : ''} in view`
            : 'Review conversations that triggered classroom safety and moderation signals.'}
        </p>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {(['ALL', 'ATTENTION', 'WARNING', 'RESTRICTED'] as const).map((value) => (
          <Button
            key={value}
            variant={severityFilter === value ? 'default' : 'neutral'}
            size="sm"
            onClick={() => {
              setSeverityFilter(value)
              setFlaggedPage(0)
            }}
          >
            {value === 'ALL' ? 'All' : value[0] + value.slice(1).toLowerCase()}
            <span className="text-xs opacity-70">({filterCounts[value]})</span>
          </Button>
        ))}
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription className="flex items-center gap-3">
            <span>{error}</span>
            <button
              className="ml-auto text-sm underline underline-offset-2 hover:no-underline"
              onClick={load}
            >
              Retry
            </button>
          </AlertDescription>
        </Alert>
      )}

      {/* Table */}
      <div className="nb-shadow rounded-base overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-main text-main-foreground hover:bg-main">
              <TableHead>Student</TableHead>
              <TableHead>Guardian</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Severity</TableHead>
              <TableHead>Latest Excerpt</TableHead>
              <TableHead>Last Flagged</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableLoadingState colSpan={8} label="Loading flagged conversations..." />
            ) : filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="py-16 text-center text-sm text-foreground/40">
                  No flagged conversations found.
                </TableCell>
              </TableRow>
            ) : (
              paginatedItems.map((item) => (
                <TableRow key={item.id} className="hover:bg-main/10">
                  <TableCell className="font-base text-sm">
                    <div>
                      <div>{item.studentName || 'Unknown Student'}</div>
                      <div className="text-xs text-foreground/45">{item.classTitle || item.chatTitle}</div>
                    </div>
                  </TableCell>

                  <TableCell>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-base border-2 border-nb-border text-xs font-base bg-background">
                      {(item.guardian || 'INPUT').toLowerCase()}
                    </span>
                  </TableCell>

                  <TableCell className="text-sm text-foreground/60">
                    {item.category || '—'}
                  </TableCell>

                  <TableCell>
                    <Badge
                      variant={
                        item.severity === 'RESTRICTED'
                          ? 'danger'
                          : item.severity === 'WARNING'
                            ? 'warning'
                            : 'attention'
                      }
                    >
                      {formatSeverityLabel(item.severity || item.moderationAction)}
                    </Badge>
                  </TableCell>

                  <TableCell className="max-w-[240px] truncate text-sm text-foreground/60">
                    {item.latestExcerpt || item.content}
                  </TableCell>

                  <TableCell className="text-sm text-foreground/60 whitespace-nowrap">
                    {formatDate(item.lastFlaggedAt || item.createdAt)}
                  </TableCell>

                  <TableCell>
                    <Badge variant={item.reviewStatus === 'DISMISSED' ? 'success' : 'muted'}>
                      {item.reviewStatus || (item.reviewed ? 'REVIEWED' : 'OPEN')}
                    </Badge>
                  </TableCell>

                  <TableCell>
                    <div className="flex items-center gap-2">
                      {item.severity === 'RESTRICTED' && item.reviewStatus !== 'DISMISSED' && (
                        <Button
                          variant="neutral"
                          size="sm"
                          className="px-3 h-7 text-xs"
                          onClick={async () => {
                            const token = getAccessToken()
                            if (!token) return
                            try {
                              await reviewFlaggedItem(token, item.id, { action: 'unblock' })
                              handleReviewed(item.id, 'unblock')
                            } catch (err) {
                              toast.error(err instanceof Error ? err.message : 'Failed to unblock item')
                            }
                          }}
                        >
                          Unblock
                        </Button>
                      )}
                      <Button
                        variant="neutral"
                        size="sm"
                        className="px-3 h-7 text-xs"
                        onClick={() => setViewingItem(item)}
                      >
                        View
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {!isLoading && !error && shouldShowFlaggedPagination(filteredItems.length) ? (
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs text-foreground/45">
            Showing {flaggedPageStart}-{flaggedPageEnd} of {filteredItems.length} safety items
          </span>
          <Pagination className="w-auto mx-0">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(event) => {
                    event.preventDefault()
                    if (safeFlaggedPage > 0) setFlaggedPage((prev) => prev - 1)
                  }}
                  aria-disabled={safeFlaggedPage === 0}
                  className={safeFlaggedPage === 0 ? 'pointer-events-none opacity-40' : ''}
                />
              </PaginationItem>
              {flaggedPageNumbers.map((pageNumber, index) =>
                pageNumber === 'ellipsis' ? (
                  <PaginationItem key={`flagged-ellipsis-${index}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={pageNumber}>
                    <PaginationLink
                      href="#"
                      isActive={pageNumber === safeFlaggedPage}
                      onClick={(event) => {
                        event.preventDefault()
                        setFlaggedPage(pageNumber)
                      }}
                    >
                      {pageNumber + 1}
                    </PaginationLink>
                  </PaginationItem>
                ),
              )}
              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(event) => {
                    event.preventDefault()
                    if (safeFlaggedPage < flaggedPageCount - 1) setFlaggedPage((prev) => prev + 1)
                  }}
                  aria-disabled={safeFlaggedPage >= flaggedPageCount - 1}
                  className={safeFlaggedPage >= flaggedPageCount - 1 ? 'pointer-events-none opacity-40' : ''}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      ) : null}

      {viewingItem && (
        <ViewModal
          item={viewingItem}
          onClose={() => setViewingItem(null)}
          onReviewed={handleReviewed}
        />
      )}
    </div>
  )
}
